/**
 * Request list inside the tracker modal.
 * Loads and filters tracked jobs. Does not own the route.
 */
import React from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { getCustomerId, getProjectId } from '../../../utils/cookies';
import { RequestTrackingItemCard } from '../components/RequestTrackingItemCard';
import { useRequestTracking } from '../hooks/useRequestTracking';
import type { RequestTrackingApiConfig, RequestTrackingStatus } from '../logic/types';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'unknown', label: 'Unknown' },
];

export interface RequestTrackingPanelProps {
  title?: string;
  projectId?: string;
  pollIntervalMs?: number;
  pageSize?: number;
  showProjectScope?: boolean;
  showFilters?: boolean;
  onActiveRequestsChange?: (hasActive: boolean, activeCount: number) => void;
  apiConfig?: RequestTrackingApiConfig;
  refreshSignal?: number | string;
  /** `page`: full route with outer Container padding. `embedded`: card/split layout with internal padding + fill height. */
  layout?: 'page' | 'embedded';
}

export function RequestTrackingPanel({
  title = 'Request Tracking',
  projectId,
  pollIntervalMs = 0,
  pageSize = 25,
  showProjectScope = true,
  showFilters = true,
  onActiveRequestsChange,
  apiConfig,
  refreshSignal,
  layout = 'embedded',
}: RequestTrackingPanelProps) {
  const pageTitleOrder = layout === 'page' ? 2 : 4;
  const resolvedCustomerId = getCustomerId();
  const resolvedProjectId = projectId ?? getProjectId() ?? '';
  const [status, setStatus] = React.useState<string>('all');
  const [page] = React.useState(1);
  const filters = React.useMemo(
    () => ({
      customerId: resolvedCustomerId || undefined,
      projectId: resolvedProjectId || undefined,
      status: status as RequestTrackingStatus | 'all',
      page,
      pageSize,
    }),
    [resolvedCustomerId, resolvedProjectId, status, page, pageSize]
  );

  const {
    items,
    total,
    loading,
    refreshing,
    error,
    refresh,
    cancelRequest,
    statusCounts,
  } = useRequestTracking({
    filters,
    pollIntervalMs,
    apiConfig,
  });

  const activeRequestCount = statusCounts.queued + statusCounts.running;
  const hasActiveRequests = activeRequestCount > 0;

  React.useEffect(() => {
    onActiveRequestsChange?.(hasActiveRequests, activeRequestCount);
  }, [hasActiveRequests, activeRequestCount, onActiveRequestsChange]);

  React.useEffect(() => {
    if (refreshSignal == null) return;
    void refresh();
    const timer = window.setTimeout(() => {
      void refresh();
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [refreshSignal, refresh]);

  return (
    <Stack
      gap="md"
      p={layout === 'embedded' ? 'md' : 0}
      style={
        layout === 'embedded'
          ? { height: '100%', overflow: 'auto' }
          : undefined
      }
    >
      <Group justify="space-between" align="end">
        <Box>
          {title ? <Title order={pageTitleOrder}>{title}</Title> : null}
          <Text size="sm" c="dimmed">
            Track queued, running, and completed bulk requests across the app.
          </Text>
          {showProjectScope && resolvedCustomerId && (
            <Text size="xs" c="dimmed" mt={2}>
              Customer scope: {resolvedCustomerId}
              {resolvedProjectId && resolvedProjectId !== resolvedCustomerId
                ? ` · Project: ${resolvedProjectId}`
                : ''}
            </Text>
          )}
        </Box>
        <Group gap="xs">
          {showFilters && (
            <Select
              size="xs"
              data={STATUS_OPTIONS}
              value={status}
              onChange={(value) => setStatus(value ?? 'all')}
              w={140}
            />
          )}
          <Button size="xs" variant="default" onClick={() => void refresh()} loading={refreshing}>
            Refresh
          </Button>
        </Group>
      </Group>

      <Group gap="xs">
        <Badge color="dark" variant="light">Total: {total}</Badge>
        <Badge color="gray" variant="light">Queued: {statusCounts.queued}</Badge>
        <Badge color="blue" variant="light">Running: {statusCounts.running}</Badge>
        <Badge color="teal" variant="light">Completed: {statusCounts.completed}</Badge>
        <Badge color="red" variant="light">Failed: {statusCounts.failed}</Badge>
      </Group>

      {error && (
        <Alert color="red" title="Could not load request updates">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Card withBorder radius="md" p="md">
          <Text size="sm" c="dimmed">
            No matching requests found.
          </Text>
        </Card>
      ) : (
        <Stack gap={6}>
          {items.map((item) => (
            <RequestTrackingItemCard
              key={item.requestId}
              item={item}
              isRefreshing={refreshing}
              onCancel={(requestId) => {
                void cancelRequest(requestId)
                  .then(() => {
                    notifications.show({
                      title: 'Request cancelled',
                      message: `Cancelled request ${requestId}`,
                      color: 'teal',
                    });
                  })
                  .catch(() => {
                    // API layer handles error notification.
                  });
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
